import type { Alarm, AlarmLocation } from '../services/alarmService';
import { parseGeometryPoint } from './geo';

/**
 * Where an alarm's map position came from, best first: the ping the handset sent
 * as the alarm was raised, then the guard's last reported position, then the site.
 */
export type AlarmPositionSource = 'trigger' | 'guard' | 'site';

export interface PlacedAlarm {
  alarm: Alarm;
  source: AlarmPositionSource;
  /** GPS accuracy of the trigger ping in metres; null for the other sources. */
  accuracyM: number | null;
  /** When the position was reported; null for a site, which does not move. */
  positionedAt: string | null;
}

export interface AlarmPin {
  /** Id of the first alarm placed here, so the marker survives the pin moving. */
  key: string;
  latitude: number;
  longitude: number;
  entries: PlacedAlarm[];
}

export interface GuardPosition {
  guardId: string;
  latitude: number;
  longitude: number;
  updatedAt: string | null;
}

interface ResolvedPosition extends Omit<PlacedAlarm, 'alarm'> {
  latitude: number;
  longitude: number;
}

// (0, 0) is how guardLocationService reports a position it could not parse.
function isUsablePosition(latitude: number, longitude: number): boolean {
  return Number.isFinite(latitude)
    && Number.isFinite(longitude)
    && (latitude !== 0 || longitude !== 0);
}

function resolvePosition(
  alarm: Alarm,
  location: AlarmLocation | undefined,
  guard: GuardPosition | undefined,
): ResolvedPosition | null {
  if (location && isUsablePosition(location.latitude, location.longitude)) {
    return {
      latitude: location.latitude,
      longitude: location.longitude,
      source: 'trigger',
      accuracyM: location.accuracyM,
      positionedAt: location.recordedAt,
    };
  }

  if (guard && isUsablePosition(guard.latitude, guard.longitude)) {
    return {
      latitude: guard.latitude,
      longitude: guard.longitude,
      source: 'guard',
      accuracyM: null,
      positionedAt: guard.updatedAt,
    };
  }

  const site = parseGeometryPoint(alarm.sites?.location);
  if (site && isUsablePosition(site[0], site[1])) {
    return {
      latitude: site[0],
      longitude: site[1],
      source: 'site',
      accuracyM: null,
      positionedAt: null,
    };
  }

  return null;
}

/**
 * Places each alarm on the map. Alarms that resolve to the same spot share one
 * pin — a lone worker who stays silent raises a new welfare alarm every hour,
 * all at one position, and stacked markers would leave only the top one clickable.
 */
export function buildAlarmPins(
  alarms: Alarm[],
  locations: AlarmLocation[],
  guards: GuardPosition[],
): { pins: AlarmPin[]; unplaced: Alarm[] } {
  const locationByAlarmId = new Map(locations.map((location) => [location.alarmId, location]));
  const guardById = new Map(guards.map((guard) => [guard.guardId, guard]));
  const pinByPosition = new Map<string, AlarmPin>();
  const unplaced: Alarm[] = [];

  alarms.forEach((alarm) => {
    const resolved = resolvePosition(
      alarm,
      locationByAlarmId.get(alarm.id),
      alarm.triggered_by ? guardById.get(alarm.triggered_by) : undefined,
    );

    if (!resolved) {
      unplaced.push(alarm);
      return;
    }

    const { latitude, longitude, ...placement } = resolved;
    // Five decimals is about a metre: closer than that is the same spot on screen.
    const positionKey = `${latitude.toFixed(5)},${longitude.toFixed(5)}`;
    const pin = pinByPosition.get(positionKey);

    if (pin) {
      pin.entries.push({ alarm, ...placement });
    } else {
      pinByPosition.set(positionKey, {
        key: alarm.id,
        latitude,
        longitude,
        entries: [{ alarm, ...placement }],
      });
    }
  });

  return { pins: [...pinByPosition.values()], unplaced };
}
